import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';



const projectData = [
  {
    title: "NOC Dashboard for Clients & Internal Use",
    short: "Real-time visibility into uptime, latency, and incidents across our GPS platform.",
    tools: ["React", "FastAPI", "PostgreSQL", "WebSockets", "Docker"],
    problem: `Roie Technologies needed a centralized dashboard to monitor fleet and platform health across 5,000+ vehicles. The lack of a live dashboard made it hard for both 
    the technical team and clients to track system uptime, latency, and handle incidents proactively.`,
    solution: `We built a real-time NOC dashboard using React and FastAPI, powered by WebSockets for instant updates. Multi-user access allows Admins to manage incidents 
    while Operators view relevant logs and uptime panels. PostgreSQL stores historical data for SLA reviews.`,
    image:"/projects/traccar-architecture.svg", 
  },

  {
  title: "Self-Healing Infrastructure with AWS Lambda & CloudWatch",
  short: "Automated incident detection and remediation for EC2 and RDS using Lambda.",
  tools: ["AWS Lambda", "CloudWatch", "SNS", "Slack", "Terraform", "EC2", "RDS"],
  problem: `Infrastructure incidents such as EC2 failures or high CPU usage often required manual intervention, causing delays in recovery and increased on-call fatigue.`,
  solution: `Implemented a self-healing system using CloudWatch alarms to trigger AWS Lambda functions. The functions automatically remediate issues (restart EC2, notify
  admins, scale RDS) and send alerts via SNS or Slack. Entire solution was built and managed using Terraform for consistency and reusability.`,
  image:"/projects/cloudwatch.png", 
},

{
  title: "Multi-Environment CI/CD Pipeline (GitHub Actions + Terraform + AWS + Vultr)",
  short: "Automated deployments across dev, staging, and production environments with Terraform and GitHub Actions.",
  tools: ["GitHub Actions", "Terraform", "AWS", "Vultr", "Secrets Manager", "Approval Gates"],
  problem: `Manual deployments to different environments caused inconsistency and high deployment time. Sensitive credentials were also difficult to manage securely.`,
  solution: `Built a robust CI/CD pipeline using GitHub Actions and Terraform to manage infrastructure across dev, staging, and production. Included gated approvals 
  for prod, encrypted secrets, and notifications via Slack. Deployment time dropped from 2 days to < 1 hour.`,
  image:"/projects/cicd.jpeg",
},

{
  title: "Cost-Optimized Serverless Architecture for Event Processing",
  short: "Built scalable, event-driven microservices for onboarding and route alerts using AWS Lambda and Step Functions.",
  tools: ["AWS Lambda", "API Gateway", "DynamoDB", "Step Functions", "CloudWatch", "Terraform"],
  problem: `We needed to process high-volume, real-time events (e.g. onboarding, route violations) cost-effectively without relying on always-on servers.`,
  solution: `Designed a fully serverless system using Lambda, API Gateway, and Step Functions. Events like customer onboarding or route violations trigger orchestrated workflows. DynamoDB handles stateful storage. The architecture improved scalability and reduced costs by 40%.`,
  image:"/projects/lambda.jpg", 
}


  // Project 2, 3, 4 will go here
];




const ProjectCard = ({ project, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      className="bg-gray-900/80 backdrop-blur-lg border border-gray-700 rounded-xl mb-6 p-5 text-white shadow-lg"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <h3 className="text-lg font-semibold text-softPurple">{project.title}</h3>
        {open ? <FaChevronUp /> : <FaChevronDown />}
      </div>

      <p className="text-gray-300 mt-2">{project.short}</p>

      <div className="flex flex-wrap gap-2 mt-3">
        {project.tools.map((tool, idx) => (
          <span
            key={idx}
            className="bg-softPurple/20 text-softPurple text-xs font-semibold px-3 py-1 rounded-full border border-softPurple/40"
          >
            {tool}
          </span>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="overflow-hidden mt-4 text-sm text-gray-400"
          >
            <div className="mb-3">
              <strong className="text-white">❓ Problem:</strong> {project.problem}
            </div>
            <div className="mb-3">
              <strong className="text-white">✅ Solution:</strong> {project.solution}
            </div>

            {project.image && (
              <img
              src={project.image}
              alt={`${project.title} preview`}
              className="mt-4 rounded-lg shadow-lg max-h-64 w-full object-contain"
            />
        )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="my-20 max-w-5xl mx-auto px-4 relative z-10">
      <motion.h2
        className="text-3xl font-bold mb-8 text-softPurple text-left"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        Projects
      </motion.h2>
      {projectData.map((proj, i) => (
        <ProjectCard key={i} index={i} project={proj} />
      ))}
    </section>
  );
};

export default Projects;
